/**
 * Página de billing: exibe assinatura atual e opções de upgrade.
 * CTA de upgrade é exibido com destaque em planos inferiores.
 */
import { useQuery } from '@tanstack/react-query'
import { billingApi } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type { Plan, SubscriptionStatus } from '../../types/api'

const STATUS_BADGE: Record<SubscriptionStatus, { label: string; cls: string }> = {
  TRIAL: { label: 'Trial', cls: 'badge-yellow' },
  ATIVA: { label: 'Ativa', cls: 'badge-green' },
  INADIMPLENTE: { label: 'Inadimplente', cls: 'badge-red' },
  SUSPENSA: { label: 'Suspensa', cls: 'badge-red' },
  CANCELADA: { label: 'Cancelada', cls: 'badge-gray' },
}

const PLAN_FEATURES: Record<Plan, string[]> = {
  GRATUITO: ['1 REP', 'Até 5 funcionários', 'Coleta manual', 'Espelho básico', 'Download AFD'],
  'BÁSICO': ['Até 3 REPs', 'Até 50 funcionários', 'Coleta automática (agente)', 'Tratamento de ponto', 'Exportação de folha'],
  PROFISSIONAL: ['REPs ilimitados', 'Funcionários ilimitados', 'Multi-REP', 'API & Integrações', 'Relatórios avançados', 'Suporte prioritário'],
}

export function BillingPage() {
  const { data: sub, isLoading } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: billingApi.subscription,
  })

  if (isLoading) return <PageLoading />

  const currentPlan = sub?.plan ?? 'GRATUITO'
  const status = sub?.status ?? 'TRIAL'
  const badgeInfo = STATUS_BADGE[status]

  return (
    <div>
      <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: 'var(--space-8)' }}>
        Plano & Billing
      </h1>

      {/* Status atual */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
          Assinatura atual
        </h2>
        <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>Plano</div>
            <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-primary)' }}>
              {currentPlan}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>Status</div>
            <span className={`badge ${badgeInfo.cls}`} style={{ marginTop: 'var(--space-1)', display: 'inline-flex' }}>
              {badgeInfo.label}
            </span>
          </div>
          {sub?.periodo_fim && (
            <div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>Válido até</div>
              <div style={{ fontWeight: 500 }}>
                {new Date(sub.periodo_fim).toLocaleDateString('pt-BR')}
              </div>
            </div>
          )}
        </div>

        {status === 'INADIMPLENTE' && (
          <div className="alert alert-danger" style={{ marginTop: 'var(--space-4)' }}>
            Seu pagamento está pendente. Regularize para manter o acesso completo.
          </div>
        )}
      </div>

      {/* Grade de planos */}
      <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
        Planos disponíveis
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 'var(--space-4)',
        }}
      >
        {(['GRATUITO', 'BÁSICO', 'PROFISSIONAL'] as Plan[]).map((plan) => (
          <PlanCard
            key={plan}
            plan={plan}
            features={PLAN_FEATURES[plan]}
            isCurrent={plan === currentPlan}
          />
        ))}
      </div>

      <p style={{ marginTop: 'var(--space-6)', fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)' }}>
        Para mudanças de plano, entre em contato com o suporte ou acesse o painel de pagamentos.
        {/* TODO: integrar link do portal de billing (Stripe/Asaas) após ADR 0003 */}
      </p>
    </div>
  )
}

function PlanCard({
  plan,
  features,
  isCurrent,
}: {
  plan: Plan
  features: string[]
  isCurrent: boolean
}) {
  const isPro = plan === 'PROFISSIONAL'

  return (
    <div
      className="card"
      style={{
        borderColor: isPro ? 'var(--color-primary)' : isCurrent ? 'var(--color-success)' : undefined,
        borderWidth: isPro || isCurrent ? '2px' : '1px',
        position: 'relative',
      }}
    >
      {isCurrent && (
        <span
          className="badge badge-green"
          style={{ position: 'absolute', top: 'var(--space-3)', right: 'var(--space-3)' }}
        >
          Plano atual
        </span>
      )}
      {isPro && !isCurrent && (
        <span
          className="badge badge-blue"
          style={{ position: 'absolute', top: 'var(--space-3)', right: 'var(--space-3)' }}
        >
          Recomendado
        </span>
      )}

      <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-4)', color: isPro ? 'var(--color-primary)' : undefined }}>
        {plan}
      </h3>

      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {features.map((f) => (
          <li key={f} style={{ display: 'flex', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
            <span style={{ color: 'var(--color-success)', flexShrink: 0 }}>✓</span>
            {f}
          </li>
        ))}
      </ul>

      {!isCurrent && (
        <button
          className={`btn ${isPro ? 'btn-primary' : 'btn-ghost'}`}
          style={{ marginTop: 'var(--space-6)', width: '100%' }}
          onClick={() => alert('Entre em contato com o suporte para alterar o plano.')}
        >
          {isPro ? 'Fazer upgrade →' : 'Selecionar plano'}
        </button>
      )}
    </div>
  )
}
